import React, {useState} from 'react';
import {Tabs} from 'antd-mobile';
import {Table} from 'antd';
import { t } from '../../../i18n';
import { formatLocalizedTime } from '../../../utils/dateFormatUtils';

export type ScanRecord = {
    id?: string;
    value1?: string;
    value2?: string;
    time?: string;
    failedReason?: string;
    seq?: number;
}

export const createRecord = (record: ScanRecord) : ScanRecord => {
    return { id: Date.now().toString() + '-' + Math.random().toString(36).slice(2, 11), time: formatLocalizedTime(), ...record};
}

/**
 * 按 seq 有序插入记录（seq 大的在前 = 最新扫描在列表顶部）。
 * 如果 record 没有 seq 字段，则 prepend 到列表前面。
 */
export const insertRecordBySeq = (prev: ScanRecord[], record: ScanRecord): ScanRecord[] => {
    if (record.seq == null) return [record, ...prev];
    const idx = prev.findIndex(r => (r.seq ?? 0) < record.seq!);
    if (idx === -1) return [...prev, record];
    return [...prev.slice(0, idx), record, ...prev.slice(idx)];
}

export interface Column {
    title: string;
    key: string;
}

export interface ResultTableProps {
    successRecords: ScanRecord[];
    failedRecords: ScanRecord[];
    successColumns: Column[];
    failedColumns: Column[];
}

export const ResultTable: React.FC<ResultTableProps> = ({
                                                            successRecords,
                                                            failedRecords,
                                                            successColumns,
                                                            failedColumns,
                                                        }) => {
    const [activeTab, setActiveTab] = useState('success');

    const successCount = successRecords.length;
    const failedCount = failedRecords.length;
    // 准备成功记录的数据
    const successData = successRecords.map(record => ({
        key: record.id,
        ...record,
    }));

    // 准备失败记录的数据（包含失败原因）
    const failedData = failedRecords.map(record => ({
        key: record.id,
        ...record,
    }));

    return (
        <>
            {/* 选项卡 */}
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                className="border-b"
            >
                <Tabs.Tab title={`${t('pda.common.succeed')}(${successCount})`} key="success"/>
                <Tabs.Tab title={`${t('pda.common.failed')}(${failedCount})`} key="failed"/>
            </Tabs>

            {/* 数据列表 */}
            <div className="flex-1 overflow-auto">
                {activeTab === 'success' ? (
                    successRecords.length > 0 ? (
                        <Table
                            columns={successColumns.map(column => ({
                                title: column.title,
                                dataIndex: column.key,
                                key: column.key,
                                onHeaderCell: () => ({
                                    className: 'whitespace-normal break-words'
                                }),
                                render: (text: string, record: any, index: number) => {
                                    // 对nextAction字段进行国际化处理
                                    let displayText = text;
                                    if (column.key === 'nextAction' && text) {
                                        displayText = t(`pda.common.exceptionActions.${text}`);
                                    }
                                    
                                    return (
                                        <div className="whitespace-normal break-words break-all">
                                            {displayText}
                                        </div>
                                    );
                                },
                            }))}
                            dataSource={successData}
                            size="small"
                        />
                    ) : (
                        <div className="p-4 text-center text-gray-500">{t('pda.common.noRecords')}</div>
                    )
                ) : (
                    failedRecords.length > 0 ? (
                        <Table
                            columns={failedColumns.map(column => ({
                                title: column.title,
                                dataIndex: column.key,
                                key: column.key,
                                onHeaderCell: () => ({
                                    className: 'whitespace-normal break-words'
                                }),
                                render: (text: string, record: any, index: number) => {
                                    // 对nextAction字段进行国际化处理
                                    let displayText = text;
                                    if (column.key === 'nextAction' && text) {
                                        displayText = t(`pda.common.exceptionActions.${text}`);
                                    }
                                    
                                    return (
                                        <div className="whitespace-normal break-words break-all">
                                            {displayText}
                                        </div>
                                    );
                                },
                            }))}
                            dataSource={failedData}
                            size="small"
                        />
                    ) : (
                        <div className="p-4 text-center text-gray-500">{t('pda.common.noRecords')}</div>
                    )
                )}
            </div>
        </>
    )
}